import { startCase } from 'es-toolkit';
import React, { useState } from 'react';
import {
  Alert,
  Autocomplete,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { EMAIL_REGEX, PropertyTextInput, useHiddenFileSelect } from '@paragon/client-tools';
import { Region, SharedComponentSet } from '@paragon/contract-library';
import { sealingService } from '@paragon/data-connections';
import { ENGINEERING_ORGANIZATION_REGION_DEFINITIONS } from '@paragon/login-tools';
import { enqueueError, enqueueSuccess, InlineNotification } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';
import { assertExhaustiveSwitch, formatAsNaturalLanguageList, notNullish } from '@paragon/utilities';

import { createJobFromSharedComponentSet, JobCreationPromise } from '../../../store/actions/sharedComponentSetActions';
import { getAnalysisSetsMap } from '../../../store/selectors/analysisSetsSelectors';
import { getProjectLayoutModel } from '../../../store/selectors/getProjectLayoutModel';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DialogTitleWithBackButton } from './DialogTitleWithBackButton';

interface Props {
  readonly sharedComponentSet: SharedComponentSet;

  readonly onNavigationBackRequested: () => void;
  readonly onSendEmailRequested: () => void;

  readonly regions: 'loading' | 'error' | readonly Region[];
  readonly visibleOrganizations: 'loading' | 'error' | readonly string[];
}

export function CreateJobForm({
  sharedComponentSet,
  onNavigationBackRequested,
  onSendEmailRequested,
  regions,
  visibleOrganizations,
}: Props) {
  const dispatch = useAppDispatch();

  const analysisSetsByGuid = useAppSelector(getAnalysisSetsMap);
  const overCapacityOrErroredComponentNames = sharedComponentSet.analysisSetGuidsWithQuantities
    .map(({ analysisSetGuid }) => {
      const analysisSet = analysisSetsByGuid.get(analysisSetGuid);
      return analysisSet && (analysisSet.capacity > 1 || analysisSet.errors.length > 0)
        ? analysisSet.componentDesign.component.name
        : null;
    })
    .filter(notNullish);

  const [renderHiddenSelect, selectFile] = useHiddenFileSelect('*', true);

  // FIXME: ideally we would instead use the project snapshot from the shared component set
  const projectLayoutModel = useAppSelector(getProjectLayoutModel);

  const [jobType, setJobType] = useState<'Seal' | 'Repair'>('Seal');

  const requestingWhat = (() => {
    switch (jobType) {
      case 'Seal':
        return 'seals';

      case 'Repair':
        return 'a repair';

      default:
        assertExhaustiveSwitch(jobType);
    }
  })();

  const [chosenOrganization, setChosenOrganization] = useState<string | null>(null);

  const regionFromProject =
    Array.isArray(regions) && projectLayoutModel && regions.find((region) => region.name === projectLayoutModel.region);

  const [chosenRegions, setChosenRegions] = useState<Region[]>(regionFromProject ? [regionFromProject] : []);
  const [validatingChosenRegions, setValidatingChosenRegions] = useState(false);

  const [contactName, setContactName] = useState<string | null>(projectLayoutModel?.contactName ?? null);
  const [contactPhone, setContactPhone] = useState<string | null>(
    // Free user accounts have this dummy phone number, which we don't want to pre-fill as it is incorrect.
    projectLayoutModel?.contactPhone && projectLayoutModel.contactPhone !== '555-555-5555'
      ? projectLayoutModel.contactPhone
      : null,
  );
  const [contactEmail, setContactEmail] = useState<string | null>(projectLayoutModel?.contactEmail ?? null);
  const [comments, setComments] = useState<string | null>(null);

  const [validate, setValidate] = useState(false);

  const [jobCreationPromiseWhileWaitingForMiscFiles, setJobCreationPromiseAndRequestMiscFiles] = useState<
    false | JobCreationPromise
  >(false);

  async function waitForAndReportResultOfJobCreation(jobCreationPromise: JobCreationPromise) {
    try {
      const result = await jobCreationPromise;
      if (result.error) {
        dispatch(enqueueError(result.userVisibleMessage));
      } else {
        dispatch(enqueueSuccess(result.userVisibleMessage, true));
      }
    } catch (error) {
      console.error(error);
      dispatch(enqueueError('Unknown error creating job. Please contact Paragon Customer Support.'));
    }
  }

  if (regions === 'loading' || visibleOrganizations === 'loading') {
    return (
      <Dialog insistent>
        <CircularProgress />
      </Dialog>
    );
  }

  if (regions === 'error' || visibleOrganizations === 'error')
    return (
      <Dialog variant="design" cancelText="Close" onCancel="close">
        <Alert variant="outlined" severity="error">
          Failed to load engineering organizations. Please try again, or contact Paragon Customer Support.
        </Alert>
      </Dialog>
    );

  const eligibleOrganizations = visibleOrganizations.filter(
    (organization) =>
      organization in ENGINEERING_ORGANIZATION_REGION_DEFINITIONS &&
      chosenRegions.every((region) => ENGINEERING_ORGANIZATION_REGION_DEFINITIONS[organization].includes(region.name)),
  );

  if (jobCreationPromiseWhileWaitingForMiscFiles) {
    return (
      <Dialog
        insistent
        fullWidth
        maxWidth="xs"
        title="Upload Miscellaneous Files?"
        cancelText="No thanks"
        onCancel={async () => {
          await waitForAndReportResultOfJobCreation(jobCreationPromiseWhileWaitingForMiscFiles);
          dispatch(closeCurrentPopup());
        }}
        submitText="Upload Files"
        onSubmit={async () => {
          const uploadedFiles = await selectFile();

          const jobCreationResult = await jobCreationPromiseWhileWaitingForMiscFiles;

          if (jobCreationResult.error === false) {
            try {
              await sealingService.jobs.addMiscellaneousFiles(
                jobCreationResult.jobId,
                uploadedFiles.map((file) => ({
                  url: file.contents,
                  name: file.filetype == null ? file.filename : `${file.filename}.${file.filetype}`,
                })),
              );
            } catch (error) {
              console.error(error);
              dispatch(
                enqueueError(
                  'Your job was created successfully, but there was an error processing miscellaneous files. Please contact Paragon Customer Support.',
                ),
              );

              dispatch(closeCurrentPopup());
              return;
            }
          }

          await waitForAndReportResultOfJobCreation(jobCreationPromiseWhileWaitingForMiscFiles);
          dispatch(closeCurrentPopup());
        }}
      >
        {renderHiddenSelect()}

        <Alert variant="outlined" severity="info">
          Miscellaneous files give repair engineers additional information. If you have photos of the repairs or files
          from other software, those can be added here.
        </Alert>
      </Dialog>
    );
  }

  if (chosenOrganization) {
    return (
      <Dialog
        insistent
        variant="design"
        hasTransition={false}
        title={
          <DialogTitleWithBackButton
            title={`Request ${startCase(requestingWhat)}`}
            onBackClick={() => {
              setChosenOrganization(null);
            }}
          />
        }
        onCancel="close"
        submitText={`Request ${startCase(requestingWhat)}`}
        onSubmit={async () => {
          if (
            chosenRegions.length > 0 &&
            contactName != null &&
            contactPhone != null &&
            contactEmail != null &&
            EMAIL_REGEX.test(contactEmail)
          ) {
            const jobCreationPromise = dispatch(
              createJobFromSharedComponentSet(
                jobType,
                sharedComponentSet,
                chosenOrganization,
                {
                  regions: chosenRegions.map((region) => region.name),
                  contactName,
                  contactPhone,
                  contactEmail,
                },
                comments,
              ),
            );

            if (jobType === 'Repair') {
              setJobCreationPromiseAndRequestMiscFiles(jobCreationPromise);
            } else {
              dispatch(closeCurrentPopup());
              await waitForAndReportResultOfJobCreation(jobCreationPromise);
            }
          } else {
            setValidate(true);
          }
        }}
        contentSx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,

          '& > *:not(:first-child)': { marginTop: '0!important', marginBottom: '0!important' },
        }}
      >
        <ToggleButtonGroup
          exclusive
          value={jobType}
          onChange={(_, newJobType) => {
            if (newJobType) setJobType(newJobType);
          }}
        >
          <ToggleButton value="Seal">Request Seals</ToggleButton>
          <ToggleButton value="Repair">Request A Repair</ToggleButton>
        </ToggleButtonGroup>

        <Divider />

        <PropertyTextInput disabled property="Engineering Organization" value={chosenOrganization} />

        <Autocomplete
          disabled
          multiple
          size="small"
          options={regions}
          getOptionLabel={(option) => option.name}
          value={chosenRegions}
          renderInput={(params) => <TextField {...params} label="Region(s)" variant="outlined" />}
        />

        <PropertyTextInput
          property="Contact Name *"
          required={validate}
          value={contactName ?? ''}
          submit={setContactName}
        />

        <PropertyTextInput
          property="Contact Phone *"
          required={validate}
          value={contactPhone ?? ''}
          submit={setContactPhone}
        />

        <PropertyTextInput
          property="Contact Email *"
          required={validate}
          error={validate && (contactEmail == null || !EMAIL_REGEX.test(contactEmail))}
          value={contactEmail ?? ''}
          submit={setContactEmail}
        />

        <PropertyTextInput property="Comments" value={comments ?? ''} submit={setComments} multiline />

        <Typography variant="body1" color="textSecondary" align="right">
          Fields with * are required.
        </Typography>
      </Dialog>
    );
  }

  return (
    <Dialog
      insistent
      maxWidth="xs"
      variant="design"
      hasTransition={false}
      title={
        <DialogTitleWithBackButton
          title={`Request ${startCase(requestingWhat)}`}
          onBackClick={onNavigationBackRequested}
        />
      }
      onCancel="close"
      contentSx={createScrollbarStyling}
    >
      {overCapacityOrErroredComponentNames.length > 0 && (
        <InlineNotification type="warning" sx={{ mb: 1 }}>
          These trusses are over capacity or have analysis errors: {overCapacityOrErroredComponentNames.join(', ')}
        </InlineNotification>
      )}

      <Typography variant="body1">Where would you like to send this job?</Typography>

      <Typography variant="body2" color="textSecondary">
        Pick a professional engineering organization licensed in your region, or choose to email truss design drawings
        to anyone you&apos;d like.
      </Typography>

      <Autocomplete
        multiple
        size="small"
        sx={{ marginTop: 2, marginBottom: 1 }}
        options={regions}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Region(s)"
            variant="outlined"
            required
            error={validatingChosenRegions && chosenRegions.length === 0}
          />
        )}
        value={chosenRegions}
        onChange={(_, region) => setChosenRegions(region)}
      />

      <List sx={[{ maxHeight: '30vh', overflow: 'auto' }, createScrollbarStyling]}>
        {eligibleOrganizations.length > 0 ? (
          eligibleOrganizations.map((organization) => (
            <ListItemButton
              key={organization}
              onClick={() => {
                if (chosenRegions.length > 0) {
                  setValidatingChosenRegions(false);
                  setChosenOrganization(organization);
                } else {
                  setValidatingChosenRegions(true);
                }
              }}
            >
              <ListItemText
                primary={organization}
                secondary={
                  chosenRegions.length > 0
                    ? `licensed to seal trusses in ${formatAsNaturalLanguageList(
                        chosenRegions.map((region) => region.name),
                        'and',
                      )}`
                    : `licensed to seal trusses in ${ENGINEERING_ORGANIZATION_REGION_DEFINITIONS[organization].length} regions`
                }
              />
            </ListItemButton>
          ))
        ) : (
          <Paper variant="outlined" sx={{ padding: 1 }}>
            <Typography variant="body2">
              No professional engineering organizations are currently available for these regions. Please use the email
              option below or contact us if you&apos;d like to discuss adding a specific engineering organization.
            </Typography>
          </Paper>
        )}
      </List>

      <Divider />

      <List>
        <ListItemButton>
          <ListItemText
            primary="Enter Email Address"
            secondary="Send truss design drawings to any email address"
            onClick={() => {
              onSendEmailRequested();
            }}
          />
        </ListItemButton>
      </List>
    </Dialog>
  );
}
